<template>
  <div class="pb-[6.25rem]">
    <div class="px-4 py-5 lg:px-5">
      <h3 class="co-display-9 mb-2 text-center font-bold text-co-gray-700">
        お申し込み内容の確認
      </h3>
      <div class="text-center text-co-gray-600">
        <div class="co-display-11 font-bold text-co-danger-500">
          お申し込みはまだ完了していません
        </div>
        <br>
        <span class="block md:inline">内容をご確認の上、</span>
        <span>お申し込みを確定してください。</span>
        <br>
        <FontAwesomeIcon
          :icon="faChevronDown"
          class="fa-fw co-display-6 mx-auto block text-co-primary-400"
        />
      </div>
    </div>
    <h3 class="co-display-11 mb-2 font-bold text-co-gray-600">
      お客様情報
    </h3>
    <div class="mb-5 rounded border border-co-gray-300 bg-co-gray-100 px-4 py-3 pb-1 lg:mb-6 lg:px-5 lg:py-4 lg:pb-2">
      <ul class="list-none">
        <li class="mb-0">
          <div class="co-display-14">
            {{ lastNameKana }} {{ firstNameKana }}
          </div>
        </li>
        <li class="mb-2">
          <div class="co-display-12">
            {{ lastName }} {{ firstName }}
          </div>
        </li>
        <li class="mb-2">
          <FontAwesomeIcon
            :icon="faEnvelope"
            class="fa-fw co-display-12 mr-5 text-co-gray-500 lg:mr-4"
          />{{ email }}
        </li>
        <li class="mb-2">
          <FontAwesomeIcon
            :icon="faPhone"
            class="fa-fw co-display-12 mr-5 text-co-gray-500 lg:mr-4"
          />{{ tel }}
        </li>
        <li
          v-if="birthday != null"
          class="mb-2"
        >
          <FontAwesomeIcon
            :icon="faCakeCandles"
            class="fa-fw co-display-12 mr-5 text-co-gray-500 lg:mr-4"
          />{{ birthday.getFullYear() }}/{{ birthday.getMonth() + 1 }}/{{ birthday.getDate() }}
        </li>
      </ul>
    </div>
    <h3 class="co-display-11 mb-2 font-bold text-co-gray-600">
      商品情報
    </h3>
    <div class="mb-5 rounded border border-co-gray-300 bg-co-gray-100 px-4 py-3 pb-1 lg:mb-6 lg:px-5 lg:py-4 lg:pb-2">
      <ul class="list-none">
        <li class="mb-2">
          <div class="co-display-12">
            コース： {{ course.attributes.title || course.attributes.track_title }} （第{{ cohort.attributes.number }}期）
          </div>
        </li>
        <li class="mb-2">
          <div class="co-display-12">
            プラン：{{ entryPlanTitle }}
          </div>
        </li>
        <li class="mb-2">
          <div class="co-display-12 mb-1">
            受講期間：{{ formatDate(beginsDate, '/') }}〜{{ formatDate(endsDate, '/') }}
          </div>
          <div class="co-display-14  text-co-gray-600">
            ※ 支払い期限内でのご入金で上記の受講期間となります
            <span v-if="discount && discount.title === '先割'">
              <br>
              ※ チャットサポート{{ notesForChatSupport(courseNamePath) }}{{ formatDate(chatSupportBeginsDate, '/') }}からご利用いただけます
            </span>
            <span v-else>
              <br>
              ※ チャットサポート{{ notesForChatSupport(courseNamePath) }}ご入金後すぐにご利用いただけます
            </span>
            <span v-if="!isLite()">
              <br>
              ※ メンタリング{{ notesForMentoring(courseNamePath) }}受講開始後に始まりますのでお待ちください
            </span>
          </div>
        </li>
        <li class="mb-2">
          <div class="co-display-12">
            数量：1
          </div>
        </li>
        <div v-if="showEntryReskilling">
          <li class="mb-2">
            <div class="co-display-12">
              リスキリング補助制度：
              <span v-if="isReskilling">利用する</span>
              <span v-else>利用しない</span>
            </div>
          </li>
        </div>
        <div v-if="showEntryReskillingAi">
          <li class="mb-2">
            <div class="co-display-12">
              給付金・リスキリング：
              <span v-if="isProBenefitSystem">専門実践教育訓練給付金制度を利用する</span>
              <span v-else-if="isReskilling">リスキリング補助制度を利用する</span>
              <span v-else>利用しない</span>
            </div>
          </li>
        </div>
        <li class="mb-2">
          <div class="co-display-12">
            備考：{{ remarks }}
          </div>
        </li>
      </ul>
    </div>
    <h3
      v-if="promotion || discount"
      class="co-display-11 mb-2 font-bold text-co-gray-600"
    >
      特典
    </h3>
    <div
      v-if="promotion || discount"
      class="mb-5 rounded border border-[#ff802b] bg-[#f0ad4e0d] px-4 py-3 pb-1 lg:mb-6 lg:px-5 lg:py-4 lg:pb-2"
    >
      <ul class="mb-0 list-none">
        <li
          v-if="promotion"
          class="mb-2"
        >
          <div class="co-display-12 te-text-confirm">
            {{ promotion }}
          </div>
        </li>
        <li
          v-if="discount"
          class="mb-2"
        >
          <div class="co-display-12 te-text-confirm">
            {{ discount.title }}
          </div>
        </li>
      </ul>
    </div>

    <h3 class="co-display-11 mb-2 font-bold text-co-gray-600">
      受講料内訳
    </h3>
    <div class="mb-5 rounded border border-co-gray-300 bg-co-gray-100 px-4 py-3 pb-1 lg:mb-6 lg:px-5 lg:py-4 lg:pb-2">
      <ul class="list-none border-b">
        <li class="mb-2 flex justify-between">
          <div class="co-display-12">
            受講料（税別）
          </div>
          <span>
            {{ Number(confirmationItems.original).toLocaleString() }}円
          </span>
        </li>
        <li
          v-if="discount"
          class="mb-2 flex justify-between"
        >
          <div class="co-display-12 text-[#ff802b]">
            特典割引
          </div>
          <span class="text-[#ff802b]">
            - {{ Number(discount.discount).toLocaleString() }}円
          </span>
        </li>
        <li class="mb-2 flex justify-between">
          <div class="co-display-12">
            消費税
          </div>
          <span>
            {{ Number(confirmationItems.tax).toLocaleString() }}円
          </span>
        </li>
      </ul>
      <div class="my-2 text-right">
        <span class="mr-4 font-bold">合計</span>
        <span class="font-bold">{{ Number(confirmationItems.total).toLocaleString() }}円</span>
      </div>
    </div>

    <h4 class="co-display-13 mb-2 font-bold text-co-gray-600">
      お支払い方法
    </h4>
    <ul class="co-display-13 mb-4 list-inside list-disc pl-7 text-co-gray-600">
      <li class="mr-0 lg:mr-10">
        クレジットカード払い
      </li>
      <li class="mr-0 lg:mr-10">
        銀行振込
      </li>
      <li class="mr-0 lg:mr-10">
        Amazon Pay
      </li>
      <li class="mr-0 lg:mr-10">
        PayPay（100万円以下で利用可能）
      </li>
      <li class="mr-0 lg:mr-10">
        コンビニ決済（30万円以下で利用可能）
      </li>
    </ul>
    <div class="co-display-14 mb-4 text-co-gray-600">
      ※ お申し込み完了後、自動的に決済ページへ画面遷移いたします。決済ページでお支払い方法をご選択いただき、申込当日（申込当日が土日祝日の場合は翌営業日を申込当日とする）を含めて4営業日以内にお支払いをお願いいたします。
    </div>

    <h4 class="co-display-13 mb-2 font-bold text-co-gray-600">
      キャンセルポリシー
    </h4>
    <div class="co-display-14 text-co-gray-600">
      受講開始の前後にかかわらず、学習システム（カリキュラム）にログインされた後のご返金・コース変更はいたしかねますのでご了承ください。なお、ログイン実施前かつ、受講開始日の1営業日前までであれば、振込手数料を引いて全額返金いたします。（受付：平日10時〜19時）
    </div>
  </div>
</template>

<script lang="ts">
import { formatDate } from '~/utility/utility'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faChevronDown, faEnvelope, faPhone, faCakeCandles } from '@awesome.me/kit-65d95819ce/icons/classic/solid'

interface Props {
  firstName: string
  lastName: string
  firstNameKana: string
  lastNameKana: string
  email: string
  tel: string
  birthday: Date | null
  course: object
  cohort: object
  entryPlanTitle: string
  remarks: string
  isProBenefitSystem: boolean | null
  isReskilling: boolean | null
  confirmationItems: object
  closesBeforeDay: Date | null
  promotion: string | null
  courseNamePath: string
  showEntryReskilling: boolean | null
  showEntryReskillingAi: boolean | null
}

interface Data {
  beginsDate: Date
  endsDate: Date
  chatSupportBeginsDate: Date
  discount: object | null
}

interface Methods {
  formatDate: (date: Date, format: string) => string
  open: () => void
  notesForMentoring: (path: string) => string
  notesForChatSupport: (path: string) => string
  isLite: () => void
}

const options = {
  async setup() {
    return {
      faChevronDown,
      faEnvelope,
      faPhone,
      faCakeCandles,
    }
  },
  props: {
    firstName: {
      default: '',
    },
    lastName: {
      default: '',
    },
    firstNameKana: {
      default: '',
    },
    lastNameKana: {
      default: '',
    },
    tel: {
      default: '',
    },
    birthday: {
      default: null,
    },
    course: {
      default: {},
    },
    cohort: {
      default: {},
    },
    entryPlanTitle: {
      default: '',
    },
    remarks: {
      default: '',
    },
    email: {
      default: '',
    },
    closesBeforeDay: {
      default: null,
    },
    promotion: {
      default: '',
    },
    isProBenefitSystem: {
      default: null,
    },
    isReskilling: {
      default: null,
    },
    confirmationItems: {
      default: {},
    },
    courseNamePath: {
      default: '',
    },
    showEntryReskilling: {
      default: false,
    },
    showEntryReskillingAi: {
      default: false,
    },
  },
  data() {
    return {
      beginsDate: new Date(),
      endsDate: new Date(),
      chatSupportBeginsDate: new Date(),
      discount: null,
    }
  },
  methods: {
    formatDate(date: Date, format: string) {
      return formatDate(date, format)
    },
    notesForMentoring(courseNamePath: string) {
      return [
        'first-sidejob',
        'frontend-sidejob-set',
        'web-production-mom',
      ].includes(courseNamePath)
        ? 'は'
        : '・課題提出は'
    },
    notesForChatSupport(courseNamePath: string) {
      return [
        'first-sidejob',
        'frontend-sidejob-set',
        'web-production-mom',
      ].includes(courseNamePath)
        ? '・課題提出は'
        : 'は'
    },
    isLite() {
      return this.entryPlanTitle.includes('Lite')
    },
    updateConfirmationItems() {
      this.beginsDate = new Date(this.confirmationItems.begins_at)
      this.endsDate = new Date(this.confirmationItems.ends_at)
      this.chatSupportBeginsDate = new Date(this.confirmationItems.chat_support_begins_at)
      this.discount = this.confirmationItems.discount
    },
  },
  async mounted() {
    this.updateConfirmationItems()
  },
  watch: {
    confirmationItems: {
      handler: 'updateConfirmationItems',
      deep: true,
    },
  },
  components: {
    FontAwesomeIcon,
  },
}

export default defineNuxtComponent(options)
</script>

<style scoped>
.te-bg-confirm-campaign {
  background: rgba(240, 173, 78, 0.05);
}

.te-text-confirm {
  color: #ff802b;
}

.te-border-confirm {
  border: 1px solid #ff802b !important;
}
</style>
